import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useSquadre } from "../../hooks/useFase7";

import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";
interface Invito {
  id: string;
  email_destinatario: string;
  ruolo_lega: string;
  is_fondatore: boolean;
  codice_leggibile: string;
  /** Esposto SOLO al creatore (admin) post-creazione/rinvio, per fallback
   * manuale se l'email Resend fallisce (es. dominio non ancora verificato).
   * NON viene mai esposto nel GET /admin/inviti (lista). */
  token?: string | null;
  scadenza: string;
  usato: boolean;
  usato_at: string | null;
  revocato: boolean;
  created_at: string;
}

const RUOLI: { value: string; label: string }[] = [
  { value: "partecipante", label: tr("Partecipante") },
  { value: "admin", label: tr("Admin Fantacalcio") },
  { value: "tesoriere", label: tr("Tesoriere") },
  { value: "mod_tm", label: tr("Moderatore Transfermarkt") },
  { value: "mod_mercato", label: tr("Moderatore Fantamercato") },
];

export function InvitiPage() {
  useAuth(); // session check
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [ruolo, setRuolo] = useState("partecipante");
  const [isFondatore, setIsFondatore] = useState(false);
  const [squadraPlaceholder, setSquadraPlaceholder] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copiato, setCopiato] = useState(false);

  // Squadre disponibili come placeholder per il nuovo presidente: quelle
  // senza presidente_user_id (le "TBD"). Andrea 2026-05-19: se non
  // assegni una squadra all'invito, l'utente accetta ma resta orfano.
  const { data: squadre } = useSquadre();
  const squadreDisponibili = (squadre ?? []).filter((s) => !s.presidente_user_id);

  // Link completo per l'invito appena creato (workaround Resend non
  // configurato: Andrea copia il link e lo manda manualmente via Discord/
  // WhatsApp al destinatario).
  const inviteLink = createdToken
    ? `${window.location.origin}/register?token=${createdToken}`
    : null;

  async function copiaLink() {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      // Fallback: select all + execCommand (browser vecchi)
      alert(inviteLink);
    }
  }

  const { data: inviti, isLoading } = useQuery({
    queryKey: ["inviti"],
    queryFn: async () => {
      const res = await api.get<Invito[]>("/admin/inviti");
      return res.data;
    },
  });

  const createInvito = useMutation({
    mutationFn: async (payload: {
      email_destinatario: string;
      ruolo_lega: string;
      is_fondatore: boolean;
      squadra_placeholder_id?: string | null;
    }) => {
      const res = await api.post<Invito>("/admin/inviti", payload);
      return res.data;
    },
    onSuccess: (data) => {
      setCreatedCode(data.codice_leggibile);
      setCreatedToken(data.token ?? null);
      setEmail("");
      setRuolo("partecipante");
      setIsFondatore(false);
      setSquadraPlaceholder("");
      setFormError(null);
      qc.invalidateQueries({ queryKey: ["inviti"] });
      qc.invalidateQueries({ queryKey: ["squadre"] });
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? tr("Errore durante la creazione dell'invito"));
    },
  });

  const revocaInvito = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/admin/inviti/${id}/revoca`);
      return id;
    },
    onSuccess: (id) => {
      // Aggiorna subito la lista (ottimistico) — il refetch/SW può tardare.
      qc.setQueryData<Invito[]>(["inviti"], (old) =>
        old ? old.map((i) => (i.id === id ? { ...i, revocato: true } : i)) : old,
      );
      qc.invalidateQueries({ queryKey: ["inviti"] });
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? tr("Errore durante la revoca dell'invito"));
    },
  });

  const rinviaInvito = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<Invito>(`/admin/inviti/${id}/rinvia`);
      return res.data;
    },
    onSuccess: (data) => {
      setCreatedCode(data.codice_leggibile);
      setCreatedToken(data.token ?? null);
      setFormError(null);
      // Aggiorna SUBITO la riga dalla risposta (nuovo codice + scadenza), così
      // la lista non resta stale finché il refetch/service worker non rinfresca
      // (Andrea 2026-06-15: serviva hard refresh per vedere il nuovo codice).
      qc.setQueryData<Invito[]>(["inviti"], (old) =>
        old ? old.map((i) => (i.id === data.id ? { ...i, ...data } : i)) : old,
      );
      qc.invalidateQueries({ queryKey: ["inviti"] });
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? tr("Errore durante il rinvio dell'invito"));
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    createInvito.mutate({
      email_destinatario: email,
      ruolo_lega: ruolo,
      is_fondatore: isFondatore,
      squadra_placeholder_id: squadraPlaceholder || null,
    });
  };

  return (
    <MobileShell logoSuffix="INVITI">
      {/* Crea invito */}
      <div className="card">
        <h2 className="font-display text-2xl text-accent mb-4 tracking-wide">
          {tr("Emetti nuovo invito")}
        </h2>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            type="email"
            placeholder={"email@destinatario.com"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="md:col-span-5 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <select
            value={ruolo}
            onChange={(e) => setRuolo(e.target.value)}
            className="md:col-span-4 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {RUOLI.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <label className="md:col-span-3 flex items-center gap-2 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={isFondatore}
              onChange={(e) => setIsFondatore(e.target.checked)}
              className="accent-accent"
            />
            <span className="text-sm">{tr("Fondatore")}</span>
          </label>

          {/* Squadra placeholder — opzionale ma raccomandato per i
                partecipanti. Andrea 2026-05-19: senza questo, l'utente
                accetta ma resta senza squadra (squadre TBD restano libere). */}
          <div className="md:col-span-12">
            <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">
              {tr("Squadra placeholder (raccomandato)")}
            </label>
            <select
              value={squadraPlaceholder}
              onChange={(e) => setSquadraPlaceholder(e.target.value)}
              className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="">{tr("— nessuna (l'utente resta senza squadra) —")}</option>
              {squadreDisponibili.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-fg-muted mt-1">
              {tr(
                "Quando l'utente accetta l'invito diventa presidente della squadra scelta (e può rinominarla gratis la 1ª volta).",
              )}
            </div>
          </div>

          <div className="md:col-span-12 flex justify-between items-center mt-1">
            {formError && <span className="text-danger text-sm">⚠️ {formError}</span>}
            <button type="submit" disabled={createInvito.isPending} className="btn-primary ml-auto">
              {createInvito.isPending ? tr("Invio…") : tr("Emetti invito")}
            </button>
          </div>
        </form>

        {createdCode && (
          <div className="mt-4 p-4 bg-success/10 border border-success/40 rounded space-y-3">
            <div>
              <div className="text-sm text-fg-muted mb-1">
                {tr("✓ Invito creato. Codice manuale:")}
              </div>
              <div className="font-mono text-2xl text-accent tracking-widest font-bold">
                {createdCode}
              </div>
            </div>

            {inviteLink && (
              <div>
                <div className="text-sm text-fg-muted mb-1">
                  {tr("🔗 Link diretto (Resend non configurato — manda tu via Discord/WhatsApp):")}
                </div>
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteLink}
                    onClick={(e) => e.currentTarget.select()}
                    className="flex-1 min-w-0 px-3 py-2 font-mono text-xs bg-primary-deep border border-primary-soft rounded-lg text-fg select-all"
                  />
                  <button
                    type="button"
                    onClick={copiaLink}
                    className="btn-primary text-sm whitespace-nowrap"
                  >
                    {copiato ? tr("✓ Copiato") : tr("📋 Copia")}
                  </button>
                </div>
                <div className="text-[10px] text-fg-muted mt-1">
                  {tr(
                    "Il link è valido fino alla scadenza dell'invito. Il destinatario lo apre, sceglie una password e l'account viene attivato.",
                  )}
                </div>
              </div>
            )}

            <div className="text-xs text-fg-muted">
              L'app prova anche a inviare l'email via Resend; se il dominio non è verificato l'email
              fallisce silenziosamente ma l'invito è comunque utilizzabile con il link sopra.
            </div>
          </div>
        )}
      </div>

      {/* Lista inviti */}
      <div className="card">
        <h2 className="font-display text-2xl text-accent mb-4 tracking-wide">
          {tr("Inviti emessi")}
        </h2>
        {isLoading && <p className="text-fg-muted">{tr("Caricamento…")}</p>}
        {inviti && inviti.length === 0 && (
          <p className="text-fg-muted text-sm">{tr("Nessun invito emesso ancora.")}</p>
        )}
        {inviti && inviti.length > 0 && (
          <div className="space-y-2">
            {inviti.map((i) => {
              const scaduto = new Date(i.scadenza) < new Date() && !i.usato;
              const stato = i.usato
                ? "usato"
                : i.revocato
                  ? "revocato"
                  : scaduto
                    ? "scaduto"
                    : "attivo";
              const statoColor = {
                usato: "bg-success/20 text-success",
                revocato: "bg-danger/20 text-danger",
                scaduto: "bg-warning/20 text-warning",
                attivo: "bg-info/20 text-info",
              }[stato];

              return (
                <div
                  key={i.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-primary-deep/50 border border-primary-soft/30"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{i.email_destinatario}</div>
                    <div className="text-xs text-fg-muted flex gap-2 mt-1 flex-wrap">
                      <span className="font-mono">{i.codice_leggibile}</span>
                      <span>·</span>
                      <span>{etichetta(i.ruolo_lega)}</span>
                      {i.is_fondatore && (
                        <>
                          <span>·</span>
                          <span className="text-accent font-bold">{tr("FONDATORE")}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>scade {new Date(i.scadenza).toLocaleDateString(localeCorrente())}</span>
                    </div>
                  </div>
                  <span className={`pill ${statoColor}`}>{stato}</span>
                  {!i.usato && !i.revocato && (
                    <>
                      <button
                        onClick={() => rinviaInvito.mutate(i.id)}
                        className="btn-ghost btn-sm"
                        title={tr("Genera nuovo codice + rinvia email")}
                      >
                        {tr("🔄 Rinvia")}
                      </button>
                      <button
                        onClick={() => revocaInvito.mutate(i.id)}
                        className="btn-ghost btn-sm text-danger border-danger"
                      >
                        {tr("🚫 Revoca")}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MobileShell>
  );
}
